import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Bell, ArrowLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { fetchNotificationPreferences, upsertNotificationPreferences } from "@/lib/notifications";
import type { NotificationPreference } from "@/lib/types";

export const Route = createFileRoute("/app/notifications")({
  component: NotificationPreferencesPage,
});

type EventKey =
  | "job_created"
  | "job_print_started"
  | "job_ready"
  | "job_on_hold"
  | "gang_sheet_updated";

const EVENTS: { key: EventKey; label: string; description: string }[] = [
  {
    key: "job_created",
    label: "New job created",
    description: "When a new job is submitted to FlightDeck",
  },
  {
    key: "gang_sheet_updated",
    label: "Gang sheet updates",
    description: "When a staff member saves or edits the gang sheet for your job",
  },
  {
    key: "job_print_started",
    label: "Print started",
    description: "When production starts on your job",
  },
  {
    key: "job_ready",
    label: "Ready for collection",
    description: "When a job is complete and ready to dispatch",
  },
  {
    key: "job_on_hold",
    label: "Job on hold",
    description: "When a job needs your attention",
  },
];

function flagFor(channel: "inapp" | "email", key: EventKey) {
  return `${channel}_${key}` as keyof NotificationPreference;
}

function NotificationPreferencesPage() {
  const { profile } = useAuth();
  const [prefs, setPrefs] = useState<Partial<NotificationPreference>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    setLoading(true);
    fetchNotificationPreferences(profile.id)
      .then((p) => setPrefs(p ?? {}))
      .finally(() => setLoading(false));
  }, [profile?.id]);

  const get = (channel: "inapp" | "email", key: EventKey): boolean => {
    const flag = flagFor(channel, key);
    const v = prefs[flag];
    return typeof v === "boolean" ? v : true;
  };

  const set = (channel: "inapp" | "email", key: EventKey, value: boolean) => {
    setPrefs((p) => ({ ...p, [flagFor(channel, key)]: value }));
  };

  const save = async () => {
    if (!profile?.id) return;
    setSaving(true);
    try {
      const payload: Partial<NotificationPreference> = {
        is_enabled: true,
        category: "job_updates",
        urgency: "normal",
        digest_frequency: "immediate",
        delivery_method: "inapp",
      };
      for (const ev of EVENTS) {
        (payload as any)[flagFor("inapp", ev.key)] = get("inapp", ev.key);
        (payload as any)[flagFor("email", ev.key)] = get("email", ev.key);
      }
      await upsertNotificationPreferences(profile.id, payload);
      toast.success("Notification preferences saved");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return <div className="text-center py-20">Please sign in.</div>;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/app/settings"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to settings
      </Link>
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-hero text-primary-foreground">
          <Bell className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notification preferences</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Choose which events you want to hear about and where they should be delivered.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-3xl border border-border bg-card shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-3 text-left font-medium">Event</th>
                <th className="px-4 py-3 text-center font-medium w-24">In-app</th>
                <th className="px-4 py-3 text-center font-medium w-24">Email</th>
              </tr>
            </thead>
            <tbody>
              {EVENTS.map((ev) => (
                <tr key={ev.key} className="border-b border-border last:border-0">
                  <td className="px-4 py-4">
                    <div className="font-medium">{ev.label}</div>
                    <div className="text-xs text-muted-foreground">{ev.description}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <Switch
                      checked={get("inapp", ev.key)}
                      onCheckedChange={(v) => set("inapp", ev.key, v)}
                    />
                  </td>
                  <td className="px-4 py-4 text-center">
                    <Switch
                      checked={get("email", ev.key)}
                      onCheckedChange={(v) => set("email", ev.key, v)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Button
        onClick={save}
        disabled={saving || loading}
        className="rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
      >
        {saving ? "Saving…" : "Save preferences"}
      </Button>
    </div>
  );
}
